import UI from "@mile-meeting/mile-ui";
import { useEffect, useState } from "react";
import AlertModal from "uis/modal/alert-modal";
import {
  getMeetingRoomPolicy,
  getMeetingRoomPolicyDetail,
  updateMeetingRoomNoShow,
  updateMeetingRoomNoShowTime,
  updateMeetingRoomPolicyDetail,
} from "apis/meeting-settings/meeting-room";
import { useToast } from "hook/useToast";
import { MainPolicySettingBlock, PolicyCustomizeSection, SubPolicySettingBlock } from "uis/Section";
import ToolTip from "uis/new-tootip";
import { MeetingRoomPolicyModal } from "uis/PolicyManage";
import { GLOBAL_MEETING_SETTING_VALUE } from "types/meeting-settings.types";
import { getToggleState } from "utils/meetingSettingUtils.service";
import { MeetingRoomSetting } from "uis/PolicyManage/MeetingRoomPolicyModal/types";
import { getAlarm } from "apis/meeting-settings/alarm";
import React from "react";
import styled from "@emotion/styled";

type NoShowPolicySettingTabProps = {
  isShow?: boolean;
};

const NoShowPolicySettingTab: React.FC<NoShowPolicySettingTabProps> = ({ isShow = false }) => {
  const { showToast } = useToast();
  const [hasMeetingRoomUsedTablet, setHasMeetingRoomUsedTablet] = useState<boolean>(false);
  const [openDetailSetting, setOpenDetailSetting] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<boolean>(false);
  const [unactiveModal, setUnactiveModal] = useState<boolean>(false);
  const [noShowActive, setNoShowActive] = useState<GLOBAL_MEETING_SETTING_VALUE>(GLOBAL_MEETING_SETTING_VALUE.OFF);
  const [noShowTime, setNoShowTime] = useState<number>(10);
  const [meetingRoomSettingDetail, setMeetingRoomSettingDetail] = useState<MeetingRoomSetting[]>([]);
  const [selectedNoshowTime, setSelectedNoshowTime] = useState<number>(10);
  const [openNoShowTimeModal, setOpenNoShowTimeModal] = useState<boolean>(false);
  const [noShowWarningTime, setNoShowWarningTIme] = useState<number>(10);

  const toggleNoShowCancellation = async (isNoShowEnabled: GLOBAL_MEETING_SETTING_VALUE) => {
    const res = await updateMeetingRoomNoShow(isNoShowEnabled);
    if (res.success) {
      setNoShowActive(isNoShowEnabled);
      showToast(res.message, "success");
    } else {
      if (res.statusCode === 409) {
        showToast(res.message, "warning");
      } else {
        showToast(res.message, "error");
      }
    }
  };

  //* 노쇼 경고 알림 시간 가져오기
  const getNoShowWarningTime = async () => {
    const res = await getAlarm();
    if (res.data) {
      setNoShowWarningTIme(res.data.noShowWarningAlarmTime);
    }
  };

  //* 노쇼 취소 처리 시간과 노쇼 경고 알림 시간 비교
  const isNoshowTimeBiggerThanWarningTime = (time: number) => {
    return time > noShowWarningTime;
  };

  //* 노쇼 취소 처리 시간 변경
  const selectNoShowTime = async (time: number) => {
    const res = await updateMeetingRoomNoShowTime(time);
    if (res.success) {
      setNoShowTime(time);
      showToast(res.message, "success");
    } else {
      showToast(res.message, "error");
    }
  };

  //* 회의실 정책 데이터 가져오기
  const setMeetingRoomPolicyData = async () => {
    const res = await getMeetingRoomPolicy();

    if (res.data) {
      setNoShowActive(res.data.isNoShowEnabled);
      setNoShowTime(res.data.noShowProcessingTime);
      setHasMeetingRoomUsedTablet(res.data.hasMeetingRoomUsedTablet);
    }
  };

  //* 회의실별 상세 설정 모달 열기
  const onHandleOpenDetailSetting = async () => {
    //* 사용중인 태블릿이 없을 경우 경고
    if (!hasMeetingRoomUsedTablet) {
      showToast("태블릿 정책은 최소 1개 이상의 회의실이 태블릿을 사용 중일 경우에만 사용 가능합니다.", "warning");
      return;
    }

    //* 노쇼 취소 기능이 비활성화 상태일 경우 경고
    if (noShowActive === GLOBAL_MEETING_SETTING_VALUE.OFF) {
      showToast("노쇼 취소 기능을 활성화해야 상세 설정이 가능합니다.", "warning");
      return;
    }

    const res = await getMeetingRoomPolicyDetail();
    if (res.data) {
      setMeetingRoomSettingDetail(res.data);
    }
    setOpenDetailSetting(true);
  };

  //* 회의실 상세 설정 저장하기
  const onHandleUpdateDetailSetting = async (values: MeetingRoomSetting[]) => {
    const res = await updateMeetingRoomPolicyDetail(values);
    if (res.success) {
      showToast("변경사항이 성공적으로 저장되었습니다.", "success");
      setMeetingRoomPolicyData();
      setOpenDetailSetting(false);
    } else {
      showToast("변경사항 저장 중 오류가 발생했습니다. 다시 시도해 주세요.", "error");
    }
  };

  useEffect(() => {
    if (isShow === false) return;
    setMeetingRoomPolicyData();
    getNoShowWarningTime();
  }, []);

  if (!isShow) return null;
  return (
    <div style={{ marginTop: "24px" }}>
      <PolicyCustomizeSection
        title="노쇼 취소 기능 설정"
        rightChildren={
          <UI.ButtonGroups.Text
            title="노쇼 취소 기능 더 알아보기"
            size="small"
            onClick={() =>
              window.open(
                "https://mile-corporation.notion.site/12ea222a9c1b819b9c04cac8e4f1d746?pvs=25#232a222a9c1b8089b0c0de51ea4603fb"
              )
            }
          />
        }
      >
        <MainPolicySettingBlock
          title="노쇼 취소 기능 활성화"
          tagLabel="내/외부 태블릿 전용"
          showTag
          description="노쇼 취소 기능은 태블릿을 사용 중인 회의실에만 적용됩니다."
          haveChildren={noShowActive !== GLOBAL_MEETING_SETTING_VALUE.OFF}
          showAdvancedSettings
          onClickAdvancedSettings={onHandleOpenDetailSetting}
          disabledAdvancedSettings={!hasMeetingRoomUsedTablet || noShowActive === GLOBAL_MEETING_SETTING_VALUE.OFF}
          rightChildren={
            <ToolTip
              text={
                noShowActive === GLOBAL_MEETING_SETTING_VALUE.ON
                  ? "전체 회의실 일괄 설정된 상태입니다."
                  : noShowActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL
                    ? "회의실별 상세 설정된 상태입니다."
                    : undefined
              }
              position={{
                top: -30,
                left: noShowActive === GLOBAL_MEETING_SETTING_VALUE.ON ? -164 : -148,
              }}
              icon={
                <UI.Selection.Toggle.TriState
                  size="large"
                  state={getToggleState(noShowActive)}
                  onChange={() => {
                    if (!hasMeetingRoomUsedTablet) {
                      showToast(
                        "태블릿 정책은 최소 1개 이상의 회의실이 태블릿을 사용 중일 경우에만 사용 가능합니다.",
                        "warning"
                      );
                      return;
                    }
                    if (noShowActive === GLOBAL_MEETING_SETTING_VALUE.OFF) {
                      setActiveModal(true);
                    } else setUnactiveModal(true);
                  }}
                  disabled={!hasMeetingRoomUsedTablet}
                />
              }
            />
          }
        />
        {noShowActive !== GLOBAL_MEETING_SETTING_VALUE.OFF && (
          <SubPolicySettingBlock
            title="노쇼 취소 처리 시간"
            description="예약 시작 시간 기준으로 설정한 시간까지 체크인이 되지 않았을 경우 노쇼 처리됩니다."
            rightChildren={
              <SelectBoxContainer>
                <UI.SelectBox
                  size="small"
                  data={["5분", "10분", "15분"]}
                  selectedData={[noShowTime !== -1 ? noShowTime + "분" : "-"]}
                  width={120}
                  dropdownMaxHeight={120}
                  onChange={(time: string[]) => {
                    if (!isNoshowTimeBiggerThanWarningTime(Number(time[0].replaceAll("분", "")))) {
                      showToast(
                        "노쇼 경고 알림 시간(현재 설정: " + noShowWarningTime + "분)보다 큰 값으로만 설정 가능합니다.",
                        "warning"
                      );
                      return;
                    }
                    setSelectedNoshowTime(Number(time[0].replaceAll("분", "")));
                    setOpenNoShowTimeModal(true);
                  }}
                  disabled={!hasMeetingRoomUsedTablet}
                />
                {noShowTime === -1 && (
                  <UI.Typography type="caption01" fontStyle="regular" color="var(--alias-color-label-alternative)">
                    회의실별 상세 설정된 상태입니다.
                  </UI.Typography>
                )}
              </SelectBoxContainer>
            }
          />
        )}
      </PolicyCustomizeSection>
      <AlertModal
        title={
          <p>
            노쇼 취소 처리 시간을
            <br />
            {noShowTime === -1 ? "일괄" : ""} 변경하시겠습니까?
          </p>
        }
        content="회의실별 상세 설정된 값이 일괄 변경됩니다."
        btnTitle="변경하기"
        modalIsOpen={openNoShowTimeModal}
        closeModal={() => setOpenNoShowTimeModal(false)}
        onChange={() => {
          selectNoShowTime(selectedNoshowTime);
          setOpenNoShowTimeModal(false);
        }}
      />
      <AlertModal
        title="노쇼 취소 기능을 활성화하시겠습니까?"
        content={
          <p style={{ textAlign: "center" }}>
            (태블릿 사용을 설정한 회의실 한정)
            <br />
            태블릿으로 회의를 시작하지 않는 경우,
            <br />
            <span style={{ color: "var(--alias-color-status-negative)" }}>
              예약 일정이 노쇼 처리로 인해 삭제됩니다.
            </span>
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          toggleNoShowCancellation(GLOBAL_MEETING_SETTING_VALUE.ON);
          setActiveModal(false);
        }}
        modalIsOpen={activeModal}
        closeModal={() => setActiveModal(false)}
      />
      <AlertModal
        title={
          <p>
            노쇼 취소 기능을
            <br />
            {noShowActive === GLOBAL_MEETING_SETTING_VALUE.PARTIAL ? "일괄" : ""} 비활성화하시겠습니까?
          </p>
        }
        modalIsOpen={unactiveModal}
        closeModal={() => setUnactiveModal(false)}
        content={
          <p style={{ textAlign: "center" }}>
            (태블릿 사용을 설정한 회의실 한정)
            <br />
            태블릿의 회의 시작 버튼을 누르지 않아도
            <br />
            예약 일정이 삭제되지 않습니다.
          </p>
        }
        btnTitle="변경하기"
        onChange={() => {
          toggleNoShowCancellation(GLOBAL_MEETING_SETTING_VALUE.OFF);
          setUnactiveModal(false);
        }}
      />
      <MeetingRoomPolicyModal
        modalIsOpen={openDetailSetting}
        closeModal={() => setOpenDetailSetting(false)}
        title="노쇼 취소 기능 회의실별 상세 설정"
        settingData={meetingRoomSettingDetail}
        onUpdate={onHandleUpdateDetailSetting}
        metaData={[
          {
            key: "isNoShowEnabled",
            label: `노쇼 취소\n기능 활성화`,
            isMainPolicy: true,
            type: "toggle",
          },
          {
            key: "noShowProcessingTime",
            label: `노쇼 취소 처리 시간`,
            type: "select",
            options: [5, 10, 15],
            minOptions: noShowWarningTime,
            disabledWhen: (data: MeetingRoomSetting) => {
              return !data["isNoShowEnabled"];
            },
          },
        ]}
      />
    </div>
  );
};

export default React.memo(NoShowPolicySettingTab);

const SelectBoxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;
